import styled, { ThemeProps } from 'styled-components';
import TextareaAutosize from 'react-textarea-autosize';
import { colors } from './colors';
import fonts from './fonts';
import { Theme } from './types';
import { P3 } from './Texts';
import React, { CSSProperties } from 'react';
import { CornerRadius, Spacing } from './spacing';
import { FlexRow } from './Row';
import { ColoredIcon } from './Icons/ColoredIcon';

export const Input = styled.input`
  outline: none;
  border: none;
  font-family: ${fonts.regular.family};
  font-weight: ${fonts.regular.weight};
  color: ${(props: ThemeProps<Theme>) => props.theme.primary};
  font-size: 15px;
  line-height: 21px;
  ::placeholder {
    color: ${colors.grey};
    opacity: 1; /* Firefox */
  }
`;

export const TextArea = styled(TextareaAutosize)`
  resize: none;
  outline: none;
  border: none;
  font-family: ${fonts.regular.family};
  font-weight: ${fonts.regular.weight};
  color: ${(props: ThemeProps<Theme>) => props.theme.primary};
  font-size: 15px;
  line-height: 21px;
  background-color: ${colors.transparent};
  ::placeholder {
    color: ${colors.grey};
    opacity: 1; /* Firefox */
  }
`;

export const FormInput = styled.input`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${CornerRadius.md}px;
  box-sizing: border-box;
  font-size: 15px;
  line-height: 21px;
  outline: none;
  color: ${(props: ThemeProps<Theme>) => props.theme.primary};
  font-family: ${fonts.regular.family};
  font-weight: ${fonts.regular.weight};
  background-color: ${(props: ThemeProps<Theme>) => props.theme.secondary};
  border: 1px solid ${(props: ThemeProps<Theme>) => props.theme.border};
  :focus {
    border: 1px solid ${colors.primary};
    outline: 1px solid ${colors.primary};
  }

  ::placeholder {
    color: ${colors.grey};
    opacity: 1; /* Firefox */
  }
`;

export const ExerciseNoteInput = styled.textarea`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  width: 100%;
  padding: ${Spacing.md}px ${Spacing.md}px ${0}px ${Spacing.md}px;
  @media (max-width: 1024px) {
    padding: ${Spacing.sm}px ${Spacing.sm}px;
  }
  height: inherit;
  border-radius: ${CornerRadius.md}px;
  box-sizing: border-box;
  resize: vertical;
  font-size: 15px;
  line-height: 21px;
  outline: none;
  font-family: ${fonts.regular.family};
  font-weight: ${fonts.regular.weight};
  color: ${(props: ThemeProps<Theme>) => props.theme.primary};
  background-color: ${(props: ThemeProps<Theme>) => props.theme.secondary};
  border: 1px solid ${(props: ThemeProps<Theme>) => props.theme.border};
  :focus {
    border: 1px solid ${colors.primary};
    outline: 1px solid ${colors.primary};
  }

  ::placeholder {
    color: ${colors.grey};
    opacity: 1; /* Firefox */
  }
`;

const Label = styled(P3)`
  font-weight: ${fonts.medium.weight};
`;

export const LabelledFormInput = (
  props: {
    label: string;
    containerStyle?: CSSProperties;
    errorMessage?: string;
    disabled?: boolean;
    onReturnPressed?: () => void;
  } & React.ComponentPropsWithRef<'input'>,
) => (
  <div style={{ position: 'relative', ...props.containerStyle }}>
    <Label style={{ marginTop: '12px', marginBottom: '4px' }}>{props.label}</Label>
    <FormInput
      onKeyDown={e => {
        if (e.key === 'Enter') {
          props.onReturnPressed?.();
        }
      }}
      style={{
        opacity: props.disabled ? 0.5 : 1.0,
        border: !!props.errorMessage ? `1px solid ${colors.red}` : undefined,
        outline: !!props.errorMessage ? `1px solid ${colors.red}` : undefined,
      }}
      {...props}
    />
    {props.errorMessage && (
      <FlexRow style={{ gap: '4px', marginTop: '6px', marginInlineStart: '3px' }}>
        <ColoredIcon type="error" />
        <P3 style={{ color: colors.red }}>{props.errorMessage}</P3>
      </FlexRow>
    )}
  </div>
);
